import { ActionType, createReducer } from 'typesafe-actions';
import { nth } from 'ramda';
import 'core-js/features/array/at';
import { IRoadItem } from 'widgets/Scoreboard/types';
import {
  addScoreboardItemAction,
  addScoreboardPredictionAction,
  removeScoreboardPredictionAction,
  resetScoreboardStateAction,
  setInitialScoreboardStateAction,
} from './actions';
import { IScoreboardData, IScoreboardState } from './types';
import {
  transformAllRoads,
  transformWinnersToInitialRoad,
  transformWinnerToRoadItem,
} from './helpers/transform';

const defaultScoreboardData: IScoreboardData = {
  data: {
    beadRoadData: [],
    bigRoadData: [],
    bigEyeBoyData: [],
    smallRoadData: [],
    cockroachPigRoadData: [],
  },
  matrixes: {
    bigRoadMatrix: [],
    bigEyeBoyMatrix: [],
    smallRoadMatrix: [],
    cockroachPigRoadMatrix: [],
  },
};

export const scoreboardState: IScoreboardState = {
  currentState: defaultScoreboardData,
  confirmedState: defaultScoreboardData,
};

const actions = {
  setInitialScoreboardStateAction,
  addScoreboardItemAction,
  addScoreboardPredictionAction,
  removeScoreboardPredictionAction,
  resetScoreboardStateAction,
};

export const scoreboardReducer = createReducer<IScoreboardState, ActionType<typeof actions>>(
  scoreboardState,
)
  .handleAction(setInitialScoreboardStateAction, (state, { payload: { winnersData } }) => {
    const { data: defaultRoads, matrixes: defaultMatrixes } = defaultScoreboardData;

    const transformedHistory = transformWinnersToInitialRoad(winnersData);

    const transformedData = transformAllRoads(transformedHistory, defaultMatrixes, defaultRoads);

    const updatedState: IScoreboardData = {
      data: {
        ...defaultRoads,
        ...transformedData.roads,
      },
      matrixes: {
        ...defaultMatrixes,
        ...transformedData.matrixes,
      },
    };

    return {
      ...state,
      currentState: updatedState,
      confirmedState: updatedState,
    };
  })
  .handleAction(resetScoreboardStateAction, (state) => ({
    ...state,
    currentState: defaultScoreboardData,
    confirmedState: defaultScoreboardData,
  }))
  .handleAction(addScoreboardItemAction, (state, { payload: { item } }) => {
    const { data: currentRoadsData, matrixes: currentMatrixes } = state.currentState;

    const newScoreboardItem = transformWinnerToRoadItem(item, currentRoadsData.beadRoadData);

    const transformedData = transformAllRoads(
      [newScoreboardItem],
      currentMatrixes,
      currentRoadsData,
    );

    const updatedState = {
      data: {
        ...currentRoadsData,
        ...transformedData.roads,
      },
      matrixes: {
        ...currentMatrixes,
        ...transformedData.matrixes,
      },
    };

    return {
      ...state,
      currentState: updatedState,
      confirmedState: updatedState,
    };
  })
  .handleAction(addScoreboardPredictionAction, (state, { payload: { winner } }) => {
    const { data: currentRoadsData, matrixes: currentMatrixes } = state.currentState;

    const newScoreboardItem: IRoadItem = {
      outcome: winner,
      isPrediction: true,
    };

    const transformedData = transformAllRoads(
      [newScoreboardItem],
      currentMatrixes,
      currentRoadsData,
    );

    return {
      ...state,
      currentState: {
        data: {
          ...currentRoadsData,
          ...transformedData.roads,
        },
        matrixes: {
          ...currentMatrixes,
          ...transformedData.matrixes,
        },
      },
    };
  })
  .handleAction(removeScoreboardPredictionAction, (state) => {
    const lastItem: IRoadItem | undefined = nth(-1, state.currentState.data.beadRoadData);

    if (lastItem && !lastItem.isPrediction) {
      return state;
    }

    return {
      ...state,
      currentState: state.confirmedState,
    };
  });
